/**
 * **KI-Schnitt** (7.66). Sascha, 3. November: „Wie könnten wir denn KI
 * Schnitt implementieren? Rohmaterial hochladen und schneiden automatisch.“
 *
 * Die Entscheidung ist ein Text-Problem: das Modell sieht nie ein Bild, nur
 * eine Liste von Abschnitten -- wer spricht, was gesagt wird, von wann bis
 * wann, in welcher Einstellung. Es antwortet mit Kennungen aus dieser Liste,
 * und nur mit denen. Was daraus Clips werden, rechnet die App selbst; ein
 * Modell, das Sekunden erfindet, kann so nichts kaputt schneiden.
 *
 * Hier liegt alles, was ohne Modell und ohne Netz geht: die Liste bauen, sie
 * als Text schreiben und die Antwort prüfen.
 */
/**
 * **Gespräch oder B-Roll** (Sascha, 4. November, gewählt: „automatisch
 * erkennen“). Der Arbeitsdienst misst beim Aufbereiten, welcher Anteil nach
 * Sprache aussieht; ab diesem Wert ist es ein Gespräch. Nicht gemessen
 * (`null`, Medien von davor) zählt als Gespräch -- lieber einmal zu viel
 * transkribiert als ein Interview übersehen.
 */
export const GESPRAECH_AB = 0.2;
export function istGespraech(sprachanteil) {
    return sprachanteil === null || sprachanteil === undefined || sprachanteil >= GESPRAECH_AB;
}
/** Die drei Vorlagen, die mitkommen. Bearbeitbar wie jede andere. */
export const KI_GRUNDVORLAGEN = [
    {
        name: 'Moderation säubern',
        daten: {
            art: 'ki',
            modus: 'herausnehmen',
            zielSekunden: null,
            anweisung: 'Das ist eine Moderation, in einem Stück aufgenommen. Nimm Versprecher, abgebrochene Sätze, ' +
                'doppelt angefangene Sätze (behalte den letzten, vollständigen Anlauf) und reine Füllwörter wie ' +
                '„äh“ und „ähm“ heraus. Alles andere bleibt, in der Reihenfolge, in der es gesagt wurde.',
        },
    },
    {
        name: 'Interview zu Beitrag',
        daten: {
            art: 'ki',
            modus: 'auswaehlen',
            zielSekunden: 90,
            anweisung: 'Das ist Rohmaterial aus einem Interview. Wähle die stärksten, verständlichsten Aussagen und ' +
                'bring sie in eine Reihenfolge, die eine Geschichte erzählt: ein Einstieg, der neugierig macht, ' +
                'der Kern, ein Schluss. Keine Fragen des Interviewers, keine halben Sätze.',
        },
    },
    {
        name: 'Langes zu Kurzvideo',
        daten: {
            art: 'ki',
            modus: 'auswaehlen',
            zielSekunden: 45,
            anweisung: 'Such aus dem Material den einen Moment, der allein für sich trägt -- eine Pointe, eine klare ' +
                'Aussage, etwas Überraschendes -- für ein Hochkant-Video in sozialen Netzen. Die ersten drei ' +
                'Sekunden müssen packen. Lieber kürzer als langatmig.',
        },
    },
];
/**
 * Die Liste der Abschnitte: ein Abschnitt je gesprochenem Satz des
 * Transkripts. Wechselt mitten im Satz die Einstellung, wird er dort geteilt,
 * an der Wortgrenze, die dem Wechsel am nächsten liegt -- sonst schnitte die
 * App später mitten durch eine Einstellung, ohne es zu wissen.
 */
export function abschnitteAus(quellen) {
    const raus = [];
    for (const q of quellen) {
        const schnitte = [...(q.schnitte ?? [])].sort((a, b) => a - b);
        const einstellungBei = (s) => 1 + schnitte.filter((x) => x <= s + 1e-6).length;
        for (const seg of q.transkript.segments) {
            const text = seg.text.trim();
            if (!text || !(seg.end > seg.start))
                continue;
            const innen = schnitte.filter((x) => x > seg.start + 0.05 && x < seg.end - 0.05);
            const worte = seg.words.filter((w) => w.end > w.start);
            if (innen.length === 0 || worte.length < 2) {
                raus.push({ id: '', medium: q.medium, von: seg.start, bis: seg.end, text, sprecher: seg.speaker, einstellung: einstellungBei(seg.start) });
                continue;
            }
            // An den Wortgrenzen teilen, die den Wechseln am nächsten liegen.
            let teil = [];
            let offen = [...innen];
            for (const w of worte) {
                if (offen.length && teil.length && w.start >= offen[0] - (w.end - w.start) / 2) {
                    raus.push(stueck(q, teil, seg.speaker, einstellungBei));
                    teil = [];
                    offen = offen.filter((x) => x > w.start);
                }
                teil.push(w);
            }
            if (teil.length)
                raus.push(stueck(q, teil, seg.speaker, einstellungBei));
        }
    }
    return raus.map((a, i) => ({ ...a, id: `a${i + 1}` }));
}
function stueck(q, worte, sprecher, einstellungBei) {
    const von = worte[0].start;
    return {
        id: '',
        medium: q.medium,
        von,
        bis: worte[worte.length - 1].end,
        text: worte.map((w) => w.text.trim()).filter(Boolean).join(' '),
        sprecher,
        einstellung: einstellungBei(von),
    };
}
/** Sekunden als „1:05.3“ -- kurz, für das Modell lesbar. */
function zeit(s) {
    const m = Math.floor(s / 60);
    return `${m}:${(s - m * 60).toFixed(1).padStart(4, '0')}`;
}
/**
 * Die Liste als Text für das Modell: eine Zeile je Abschnitt. Kein JSON
 * hinein -- Zeilen sind kürzer, und ein kleines Modell liest sie besser.
 */
export function abschnittsText(liste, namen = {}) {
    return liste
        .map((a) => {
        const wer = a.sprecher ? ` · ${a.sprecher}` : '';
        const wo = namen[a.medium] ?? a.medium;
        return `${a.id} | ${wo} E${a.einstellung} ${zeit(a.von)}–${zeit(a.bis)} (${(a.bis - a.von).toFixed(1)} s)${wer} | ${a.text}`;
    })
        .join('\n');
}
/** Die Nachricht an das Modell: Vorlage, Briefing, Liste und das Antwortformat. */
export function kiAuftrag(vorlage, briefing, liste, namen = {}) {
    const regeln = [
        'Du schneidest Video nach einem Transkript. Du siehst nur die Abschnitte unten, jede Zeile beginnt mit ihrer Kennung.',
        'Antworte ausschliesslich mit JSON in dieser Form: {"abschnitte": ["a3", "a7", ...], "begruendung": "ein Satz"}.',
        'Verwende nur Kennungen aus der Liste, jede höchstens einmal.',
        vorlage.modus === 'herausnehmen'
            ? 'Behalte die Reihenfolge der Liste bei; du darfst nur weglassen, nicht umstellen.'
            : 'Du darfst auswählen und die Reihenfolge frei bestimmen.',
    ];
    if (vorlage.zielSekunden)
        regeln.push(`Das Ergebnis soll ungefähr ${Math.round(vorlage.zielSekunden)} Sekunden lang sein.`);
    const nutzer = [
        `Auftrag: ${vorlage.anweisung}`,
        briefing.trim() ? `Briefing: ${briefing.trim()}` : '',
        '',
        'Abschnitte:',
        abschnittsText(liste, namen),
    ]
        .filter((z, i) => z !== '' || i === 2)
        .join('\n');
    return { system: regeln.join('\n'), nutzer };
}
/**
 * Die Antwort des Modells prüfen. Ein Modell darf sich irren, die App nicht:
 * unbekannte Kennungen und doppelte fallen heraus, und im Modus
 * „herausnehmen“ zählt die Reihenfolge der Liste, egal was zurückkam.
 * Gibt `null` zurück, wenn gar nichts Brauchbares darin steht.
 */
export function pruefeAntwort(roh, liste, modus) {
    const json = jsonAus(roh);
    if (!json || typeof json !== 'object')
        return null;
    const ids = json.abschnitte;
    if (!Array.isArray(ids))
        return null;
    const nachId = new Map(liste.map((a, i) => [a.id, { a, i }]));
    const gesehen = new Set();
    const verworfen = [];
    const gewaehlt = [];
    for (const roheId of ids) {
        const id = String(roheId).trim();
        const treffer = nachId.get(id);
        if (!treffer) {
            verworfen.push(`${id}: gibt es nicht`);
            continue;
        }
        if (gesehen.has(id)) {
            verworfen.push(`${id}: doppelt`);
            continue;
        }
        gesehen.add(id);
        gewaehlt.push(treffer);
    }
    if (gewaehlt.length === 0)
        return null;
    if (modus === 'herausnehmen')
        gewaehlt.sort((x, y) => x.i - y.i);
    const abschnitte = gewaehlt.map((g) => g.a);
    const begruendung = String(json.begruendung ?? '').slice(0, 500);
    return { abschnitte, begruendung, dauer: abschnitte.reduce((s, a) => s + (a.bis - a.von), 0), verworfen };
}
/** JSON aus einer Modell-Antwort holen -- auch wenn es in ```json … ``` steht oder Text drumherum hat. */
function jsonAus(roh) {
    const ohneZaun = roh.replace(/```(?:json)?/gi, '');
    const anfang = ohneZaun.indexOf('{');
    const ende = ohneZaun.lastIndexOf('}');
    if (anfang < 0 || ende <= anfang)
        return null;
    try {
        return JSON.parse(ohneZaun.slice(anfang, ende + 1));
    }
    catch {
        return null;
    }
}
/**
 * **Nachschärfen** (7.66): „kürzer“, „anderer Einstieg“. Das Modell baut auf
 * seinem letzten Vorschlag auf statt von vorn -- es bekommt ihn mit, und dazu,
 * was daran nicht passte. Was jemand von Hand abgewählt hat, steht nicht mehr
 * in diesem Vorschlag; auch das ist eine Rückmeldung.
 */
export function nachschaerfen(auftrag, vorher, rueckmeldung) {
    const was = rueckmeldung.trim();
    return {
        system: auftrag.system,
        nutzer: [
            auftrag.nutzer,
            '',
            `Dein letzter Vorschlag (so, wie er jetzt steht): ${vorher.length ? vorher.join(', ') : '(leer)'}`,
            was ? `Rückmeldung dazu: ${was}` : 'Rückmeldung dazu: noch einmal, besser.',
            'Mach einen neuen Vorschlag, der die Rückmeldung umsetzt. Gleiches Antwortformat.',
        ].join('\n'),
    };
}
//# sourceMappingURL=kischnitt.js.map